<template>
    <vx-table-row>
        <vx-table-row-title>
            <a
                :href="phoneNumberLink"
                data-test="phone-number-link"
                class="no-underline! text-inherit hover:text-sky-600"
            >
                {{ formatPhoneNumber(model.phone_number) }}
            </a>
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> Number Type: </template>
            {{ formatNumberType(model.number_type) }}
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Country: </template>
            {{ countryEmoji(model.iso_country) }} {{ model.iso_country }}
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Voice: </template>

            <template v-if="model.is_hosted"> Hosted </template>
            <template v-else-if="model.forward_voice_to"> Forward </template>
            <template v-else-if="model.voice_message_text"> Say Message </template>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Sending: </template>

            <font-awesome-icon
                widthAuto
                data-test="used-for-sending-check"
                :icon="faCircleCheck"
                v-if="model.use_for_sending"
            ></font-awesome-icon>
        </vx-table-row-item>

        <vx-table-row-item v-if="FEATURES.contact_groups">
            <template v-slot:label> Group: </template>

            <div v-if="model.group_id">
                <vx-icon-loading v-if="group === undefined" spin></vx-icon-loading>
                <template v-else>
                    <div v-if="group?.name" class="line-clamp-1 font-bold" :title="group.name">
                        {{ group.name }}
                    </div>
                    <div class="break-all text-slate-500">{{ model.group_id }}</div>
                </template>
            </div>
            <div v-else>-</div>
        </vx-table-row-item>

        <vx-table-row-item v-if="isImpersonatingOrPlatformAdmin">
            <template v-slot:label> Vendor: </template>
            {{ formatVendor(model.vendor) }}
        </vx-table-row-item>

        <vx-table-row-buttons v-if="isImpersonatingOrPlatformAdmin">
            <vx-dropdown placement="right">
                <vx-button
                    data-test="dropdown"
                    aria-label="Phone number actions"
                    type="button"
                    color="muted"
                    outline
                    class="w-[45px]"
                >
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item
                        v-if="isImpersonatingOrPlatformAdmin && !model.phone_number_pool_id"
                        data-test="add-to-pool"
                        @click="addToPoolModal = true"
                    >
                        Add to Pool
                        <font-awesome-icon widthAuto :icon="faPhoneOffice"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item
                        v-if="FEATURES.contact_groups"
                        data-test="assign-to-group"
                        @click="assignToGroupModal = true"
                    >
                        Assign to Group
                        <font-awesome-icon widthAuto :icon="faGrid2Plus"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item data-test="delete" @click="deletePhoneNumber()">
                        Delete
                        <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <vx-button :href="phoneNumberLink" data-test="edit" color="muted" outline>
                Edit <font-awesome-icon widthAuto :icon="faFilePen" />
            </vx-button>
        </vx-table-row-buttons>

        <vx-modal v-if="isImpersonatingOrPlatformAdmin" v-model:visible="addToPoolModal" size="md" padding="standard">
            <template v-slot:header> Add to Phone Pool </template>
            <template v-slot:subheader>
                Add <strong>{{ formatPhoneNumber(model.phone_number) }}</strong> to a Phone Pool.
            </template>

            <div>
                <vx-alert v-if="addToPoolError" class="mb-4" color="danger" :closeable="false">{{
                    addToPoolError
                }}</vx-alert>
                <select-phone-number-pool v-model="poolToAddTo" :clearable="false" size="lg"></select-phone-number-pool>
            </div>

            <template v-slot:footer>
                <div class="flex flex-col-reverse gap-4 lg:flex-row">
                    <vx-button
                        @click="addToPoolModal = false"
                        type="button"
                        class="grow lg:w-20 lg:grow-0"
                        size="lg"
                        color="muted"
                    >
                        Cancel
                    </vx-button>
                    <vx-button
                        @click="addToPool()"
                        data-test="confirm-add-to-pool"
                        :loading="addingToPool"
                        type="button"
                        block
                        size="lg"
                        color="primary"
                    >
                        Add
                        <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                    </vx-button>
                </div>
            </template>
        </vx-modal>

        <vx-modal v-if="FEATURES.contact_groups" v-model:visible="assignToGroupModal" size="md" :padding="false">
            <template v-slot:header>
                <div class="p-6">Confirmation</div>
            </template>

            <div class="p-6 lg:p-10">
                <select-group v-model="groupToAssignTo" :clearable="true" size="lg"></select-group>
            </div>

            <template v-slot:footer>
                <div class="flex flex-col-reverse gap-4 lg:flex-row">
                    <vx-button
                        @click="assignToGroupModal = false"
                        type="button"
                        class="grow lg:w-20 lg:grow-0"
                        size="lg"
                        color="muted"
                    >
                        Cancel
                    </vx-button>
                    <vx-button
                        @click="assignToGroup()"
                        data-test="confirm-assign-to-group"
                        :loading="assigningToGroup"
                        type="button"
                        block
                        size="lg"
                        color="primary"
                    >
                        Confirm
                        <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                    </vx-button>
                </div>
            </template>
        </vx-modal>
    </vx-table-row>
</template>

<script setup>
import {
    faCircleCheck,
    faEllipsis,
    faFilePen,
    faGrid2Plus,
    faPhoneOffice,
    faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxModal,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
    VxIconLoading,
    VxSwal,
    VxAlert,
} from '@voxie/frontend-components';
import { computed, inject, onMounted, ref } from 'vue';
import { formatPhoneNumber } from '~/components/filters';
import { useToasted } from '~/composables/useToasted';
import { formatNumberType, formatVendor } from '~/constants/phoneNumbers';
import { adminPhoneNumbersClient, groupsClient, adminPhoneNumberPoolsClient } from '~/services';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import { countryEmoji } from '~/utils/string';
import SelectGroup from '~/components/general/SelectGroup.vue';
import SelectPhoneNumberPool from '~/components/general/SelectPhoneNumberPool.vue';
import { catchRequestError, throwSwalRequestError } from '~/utils/request';

const FEATURES = inject('FEATURES');
const toasted = useToasted();

const isImpersonatingOrPlatformAdmin = Spark.isImpersonating || Spark.isPlatformAdmin;

const model = defineModel({
    type: Object,
    required: true,
});

const phoneNumberLink = computed(() => `/settings/teams/${model.value.team_id}/phone-numbers/${model.value.id}`);

const group = ref(undefined);
const emit = defineEmits(['deleted', 'addedToPool']);

const fetchGroup = async () => {
    if (!(FEATURES.contact_groups && model.value.group_id)) {
        return;
    }
    if (!model.value.group_id) {
        group.value = null;
        return;
    }
    group.value = undefined;
    try {
        const { data } = await groupsClient.getGroup({ teamId: model.value.team_id }, model.value.group_id);
        group.value = data;
    } catch {
        group.value = null;
    }
};

onMounted(() => {
    groupToAssignTo.value = model.value.group_id;
    fetchGroup();
});

const addToPoolModal = ref(false);
const poolToAddTo = ref(null);
const addingToPool = ref(false);
const addToPoolError = ref('');

const addToPool = async () => {
    if (addingToPool.value) {
        return;
    }

    addingToPool.value = true;
    try {
        await adminPhoneNumberPoolsClient.addRemovePoolNumbers(model.value.team_id, 'add', poolToAddTo.value.value, [
            model.value.id,
        ]);
        emit('addedToPool', model.value, poolToAddTo.value);
        model.value.phone_number_pool_id = poolToAddTo.value.value;
        poolToAddTo.value = null;
        addToPoolModal.value = false;
    } catch (e) {
        console.error(e);
        addToPoolError.value = catchRequestError(e);
    } finally {
        addingToPool.value = false;
    }
};

const assignToGroupModal = ref(false);
const groupToAssignTo = ref(null);
const assigningToGroup = ref(false);

const assignToGroup = async () => {
    if (assigningToGroup.value) {
        return;
    }

    assigningToGroup.value = true;
    try {
        await adminPhoneNumbersClient.update(model.value.team_id, model.value.id, {
            group_id: groupToAssignTo.value,
        });

        model.value.group_id = groupToAssignTo.value;
        fetchGroup();

        assignToGroupModal.value = false;
    } catch (e) {
        console.error(e);
        if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
        } else {
            toasted.error(e?.response?.data?.message || 'Something went wrong.');
        }
    } finally {
        assigningToGroup.value = false;
    }
};

const deletePhoneNumber = () => {
    VxSwal.confirmation('Delete Phone Number', 'Are you sure? There is no undo.', 'danger', () =>
        adminPhoneNumbersClient
            .delete(model.value.team_id, model.value.id)
            .then(() => emit('deleted', model.value.id))
            .catch(throwSwalRequestError)
    );
};
</script>
